import {DraggableHandle} from '@wandb/weave/components/DraggablePopups';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo} from 'react';
import {createPortal} from 'react-dom';
import Draggable from 'react-draggable';
import {ResizableBox, type ResizeCallbackData} from 'react-resizable';

import {useNavbarBottom} from '../../util/hooks/useNavbarBottom';
import {
  MAX_WINDOW_HEIGHT,
  MAX_WINDOW_WIDTH,
  MIN_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
} from './constants';
import {useBoundedDrag} from './useBoundedDrag';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {useViewportHeight} from './useViewportHeight';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentHeader} from './WBAgentHeader';
import {WBAgentThread} from './WBAgentThread';

const minConstraints: [number, number] = [MIN_WINDOW_WIDTH, MIN_WINDOW_HEIGHT];
const handleSize: [number, number] = [20, 20];

export const WBAgentChatWindow = () => {
  const {containerRef, nodeRef, position, handleDrag} = useBoundedDrag(true);
  const nightModeRevertFilter = useNightModeRevertFilter();
  const navbarBottom = useNavbarBottom();
  const desiredWidth = useWBAgentContextStore(s => s.windowWidth);
  const desiredHeight = useWBAgentContextStore(s => s.windowHeight);
  const setWindowSize = useWBAgentContextStore(s => s.setWindowSize);
  const viewportHeight = useViewportHeight();

  const availableHeight = viewportHeight - navbarBottom;
  const effectiveHeight = Math.max(
    MIN_WINDOW_HEIGHT,
    Math.min(desiredHeight, availableHeight)
  );

  const maxConstraints: [number, number] = useMemo(
    () => [
      Math.min(MAX_WINDOW_WIDTH, window.innerWidth * 0.75),
      Math.min(MAX_WINDOW_HEIGHT, availableHeight),
    ],
    [availableHeight]
  );

  const handleResize = useCallback(
    (_e: React.SyntheticEvent, {size: newSize}: ResizeCallbackData) => {
      setWindowSize(newSize.width, newSize.height);
    },
    [setWindowSize]
  );

  return createPortal(
    <Tailwind>
      <div
        ref={containerRef}
        className="fixed inset-x-0 bottom-0 z-[1000] pointer-events-none overflow-hidden"
        style={{top: navbarBottom}}>
        <Draggable
          handle=".handle"
          bounds="parent"
          nodeRef={nodeRef}
          position={position}
          onDrag={handleDrag}>
          <div
            ref={nodeRef}
            className="absolute top-0 left-0 pointer-events-auto rounded-[8px] flex flex-col">
            <div
              className="absolute inset-0 rounded-[8px] bg-white dark:bg-moon-850 border-[1.5px] border-moon-250 dark:border-[#363C44] shadow-[0_12px_24px_0_rgba(13,15,18,0.16)] dark:shadow-[0_12px_24px_0_rgba(13,15,18,0.32)]"
              style={{filter: nightModeRevertFilter}}
            />
            <ResizableBox
              width={desiredWidth}
              height={effectiveHeight}
              handle={
                <span className="absolute bg-transparent z-[1] hover:bg-teal-450 -bottom-4 -right-4 w-10 h-10 cursor-se-resize" />
              }
              handleSize={handleSize}
              minConstraints={minConstraints}
              maxConstraints={maxConstraints}
              onResize={handleResize}>
              <div
                className="flex flex-col"
                style={{width: desiredWidth, height: effectiveHeight}}>
                <DraggableHandle>
                  <WBAgentHeader />
                </DraggableHandle>
                <WBAgentThread />
              </div>
            </ResizableBox>
          </div>
        </Draggable>
      </div>
    </Tailwind>,
    document.body
  );
};
